import React, { useState, useEffect } from "react";
import { Rnd } from "react-rnd";
import { Save } from "lucide-react";
import API_URL from '../config';

const startHour = 6;
const endHour = 22;
const pixelsPerMinute = 2;
const defaultDuration = 120;

const timeToMinutes = (time) => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const minutesToTime = (min) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
};

export default function ModifPlanning() {
  const [employees, setEmployees] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, empIdx: null, shiftIdx: null });
  const [deletingShift, setDeletingShift] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const formattedDate = selectedDate.toISOString().split("T")[0];

  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) setContextMenu({ visible: false, x: 0, y: 0, empIdx: null, shiftIdx: null });
    };
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, [contextMenu]);

  useEffect(() => {
    const fetchShiftsAndUsers = async () => {
      const token = localStorage.getItem("token");

      try {
        const userRes = await fetch(`${API_URL}/users/`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!userRes.ok) {
          console.error("Erreur lors du chargement des utilisateurs");
          return;
        }

        const users = await userRes.json();
        const userMap = {};
        for (const user of users) {
          userMap[user.id] = {
            name: user.full_name,
            role: user.role,
          };
        }

        const shiftRes = await fetch(`${API_URL}/shifts/`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!shiftRes.ok) {
          console.error("Erreur lors du chargement des shifts");
          return;
        }

        const data = await shiftRes.json();
        const empMap = {};
        for (const shift of data) {
          const userId = shift.user_id;
          const start = new Date(shift.start_time);
          const end = new Date(shift.end_time);
          const date = start.toISOString().split("T")[0];

          if (!empMap[userId]) {
            const user = userMap[userId] || { name: `User ${userId}`, role: null };
            empMap[userId] = {
              name: user.name,
              role: user.role,
              user_id: userId,
              shifts: [],
            };
          }

          empMap[userId].shifts.push({
            id: shift.id,
            date,
            start: start.toTimeString().slice(0, 5),
            end: end.toTimeString().slice(0, 5),
            label: "Imported",
            color: "bg-blue-500",
            status: "imported",
            originalStart: start.toTimeString().slice(0, 5),
            originalEnd: end.toTimeString().slice(0, 5),
          });
        }

        setEmployees(Object.values(empMap));
      } catch (error) {
        console.error("Erreur réseau :", error);
      }
    };

    fetchShiftsAndUsers();
  }, []);

  const handleAddShift = (empIdx, clickX) => {
    const minutesFromStart = Math.floor(clickX / pixelsPerMinute);
    const start = minutesToTime(minutesFromStart + startHour * 60);
    const end = minutesToTime(minutesFromStart + defaultDuration + startHour * 60);

    const newShift = {
      date: formattedDate,
      start,
      end,
      label: "New",
      color: "bg-green-500",
      status: "new",
    };

    const newEmployees = [...employees];
    newEmployees[empIdx].shifts.push(newShift);
    setEmployees(newEmployees);
  };
  const handleDragResize = (empIdx, shiftIdx, newStartMin, newEndMin) => {
    const newEmployees = [...employees];
    const shift = newEmployees[empIdx].shifts[shiftIdx];
    const newStart = minutesToTime(newStartMin + startHour * 60);
    const newEnd = minutesToTime(newEndMin + startHour * 60);

    shift.start = newStart;
    shift.end = newEnd;

    if (
      shift.status === "imported" &&
      (shift.start !== shift.originalStart || shift.end !== shift.originalEnd)
    ) {
      shift.status = "modified";
      shift.color = "bg-yellow-500";
    }

    setEmployees(newEmployees);
  };

  const saveShifts = async () => {
    const token = localStorage.getItem("token");

    for (const emp of employees) {
      for (const shift of emp.shifts) {
        if (shift.date !== formattedDate) continue;
        if (shift.status !== "new" && shift.status !== "modified") continue;

        const payload = {
          start_time: `${formattedDate}T${shift.start}:00`,
          end_time: `${formattedDate}T${shift.end}:00`,
          user_id: emp.user_id,
        };

        try {
          const res = await fetch(`${API_URL}/shifts/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          });

          if (!res.ok) {
            const err = await res.json();
            console.error(`Erreur pour ${emp.name} :`, err);
          } else {
            const data = await res.json();
            console.log("Shift enregistré :", data);
          }
        } catch (error) {
          console.error("Erreur réseau :", error);
        }
      }
    }

    alert("Shifts enregistrés !");
  };
  const confirmDeleteShift = () => {
    if (!pendingDelete) return;
    const { empIdx, shiftIdx } = pendingDelete;
    const shiftId = `${empIdx}-${shiftIdx}`;
    setDeletingShift(shiftId);

    setTimeout(() => {
      const newEmployees = [...employees];
      newEmployees[empIdx].shifts.splice(shiftIdx, 1);
      setEmployees(newEmployees);
      setDeletingShift(null);
      setPendingDelete(null);
    }, 200);
  };

  const cancelDeleteShift = () => {
    setPendingDelete(null);
  };

  const handleDeleteShift = (empIdx, shiftIdx) => {
    setPendingDelete({ empIdx, shiftIdx });
    setContextMenu({ visible: false, x: 0, y: 0, empIdx: null, shiftIdx: null });
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <header className="flex items-center justify-between px-6 py-4 border-b">
        <h1 className="text-xl font-semibold">Planning Journalier</h1>
        <div className="flex items-center gap-4">
          <input
            type="date"
            value={formattedDate}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            className="border px-2 py-1 rounded text-sm"
          />
          <button
            className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
            onClick={saveShifts}
          >
            <Save className="w-4 h-4" /> Enregistrer
          </button>
        </div>
      </header>

      <main className="flex-1 p-6 overflow-x-auto">
        <div className="bg-white border rounded-lg min-w-fit">
          <div className="sticky top-0 z-20 bg-white">
            <div className="grid grid-cols-[200px_1fr] border-b text-xs text-gray-500">
              <div className="p-2 font-medium bg-gray-100 sticky left-0 z-30">Employé</div>
              <div className="flex">
                {Array.from({ length: endHour - startHour + 1 }, (_, i) => (
                  <div
                    key={i}
                    className="w-[120px] text-center border-l border-gray-200"
                  >
                    {(startHour + i).toString().padStart(2, "0")}:00
                  </div>
                ))}
              </div>
            </div>
          </div>
          {employees.map((emp, empIdx) => (
            <div
              key={emp.user_id}
              className="grid grid-cols-[200px_1fr] border-b h-[60px]"
            >
              <div className="px-3 py-1 bg-white text-sm flex flex-col justify-center sticky left-0 z-20 border-r">
                <span className="font-medium">{emp.name}</span>
                {emp.role === 'MANAGER' && (
                  <span className="text-[11px] text-indigo-600 bg-indigo-100 px-2 py-[2px] rounded-full w-fit mt-[2px]">
                    Manager
                  </span>
                )}
              </div>

              <div
                className="relative"
                onClick={(e) => {
                  if (
                    e.target instanceof HTMLElement &&
                    e.target.classList.contains("relative")
                  ) {
                    const x = e.nativeEvent.offsetX;
                    handleAddShift(empIdx, x);
                  }
                }}
              >
                {emp.shifts.map((shift, shiftIdx) => {
                  if (shift.date !== formattedDate) return null;
                  const start = timeToMinutes(shift.start);
                  const end = timeToMinutes(shift.end);
                  const x = (start - startHour * 60) * pixelsPerMinute;
                  const width = (end - start) * pixelsPerMinute;
                  const isDeleting = deletingShift === `${empIdx}-${shiftIdx}`;

                  return (
                    <Rnd
                      key={shiftIdx}
                      bounds="parent"
                      size={{ width, height: 40 }}
                      position={{ x, y: 10 }}
                      enableResizing={{ left: true, right: true }}
                      onDragStop={(e, d) => {
                        const newStart = Math.round(d.x / pixelsPerMinute);
                        const duration = end - start;
                        handleDragResize(empIdx, shiftIdx, newStart, newStart + duration);
                      }}
                      onResizeStop={(e, dir, ref, delta, pos) => {
                        const newStart = Math.round(pos.x / pixelsPerMinute);
                        const newEnd = Math.round((pos.x + ref.offsetWidth) / pixelsPerMinute);
                        handleDragResize(empIdx, shiftIdx, newStart, newEnd);
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setContextMenu({
                          visible: true,
                          x: e.clientX,
                          y: e.clientY,
                          empIdx,
                          shiftIdx,
                        });
                      }}
                      className={`absolute rounded-md text-white text-xs px-2 py-1 flex items-center justify-between ${shift.color} cursor-move transition-opacity duration-200 ${isDeleting ? "opacity-0" : "opacity-100"}`}
                    >
                      <div>
                        {shift.start} – {shift.end} • {shift.label}
                      </div>
                    </Rnd>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {contextMenu.visible && (
          <div
            style={{ top: contextMenu.y, left: contextMenu.x }}
            className="fixed z-50 bg-white shadow-md border rounded text-sm"
          >
            <button
              className="block px-4 py-2 hover:bg-red-100 text-red-600 w-full text-left"
              onClick={() => handleDeleteShift(contextMenu.empIdx, contextMenu.shiftIdx)}
            >
              Supprimer le shift
            </button>
          </div>
        )}

        {pendingDelete && (
          <div className="fixed bottom-4 left-4 bg-white shadow-md border px-4 py-3 rounded z-50 animate-fade-in">
            <p className="text-sm mb-2">Confirmer la suppression du shift ?</p>
            <div className="flex gap-2">
              <button
                onClick={confirmDeleteShift}
                className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
              >
                Supprimer
              </button>
              <button
                onClick={cancelDeleteShift}
                className="bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-300"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
